/**
 * Colony Trait System - 7 high-level sliders that map to internal simulation parameters.
 *
 * No artificial budget. The constraint is emergent: cranking any slider too high
 * produces natural negative consequences through the simulation itself.
 */

export interface ColonyTraitDef {
  key: string;          // 'exploration', 'recruitment', etc.
  label: string;
  description: string;  // Explains what it does AND the tradeoff
  min: number;          // 0
  max: number;          // 100
  step: number;         // 5
  defaultValue: number; // 50
  paramMap: Array<{
    targetKey: string;
    mapFn: (sliderValue: number) => number;
  }>;
}

/**
 * Linear interpolation helper.
 */
function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

/**
 * Map slider (0-100) to parameter via 3-point lerp (low, mid, high).
 */
function lerp3(low: number, mid: number, high: number, slider: number): number {
  const t = slider / 100;
  if (t <= 0.5) {
    return lerp(low, mid, t * 2);
  }
  return lerp(mid, high, (t - 0.5) * 2);
}

/**
 * All 7 colony trait definitions.
 */
export const COLONY_TRAITS: ColonyTraitDef[] = [
  {
    key: 'exploration',
    label: 'Exploration',
    description: 'High: ants scatter, discover new food but never reinforce trails. Low: ants exploit the first trail forever, missing new food sources.',
    min: 0,
    max: 100,
    step: 5,
    defaultValue: 50,
    paramMap: [
      {
        // Noise weight in foraging blend
        targetKey: '_blend.noiseWeight',
        mapFn: (s) => lerp3(0.03, 0.2, 0.6, s),
      },
      {
        // Trail confidence decay rate
        targetKey: 'parkington.pheromones.TRAIL_CONFIDENCE_DECAY',
        mapFn: (s) => lerp3(0.01, 0.05, 0.2, s),
      },
      {
        // Food odor weight in blend (higher exploration = stronger food smell attraction)
        targetKey: '_blend.foodOdorWeight',
        mapFn: (s) => lerp3(0.1, 0.3, 0.6, s),
      },
    ],
  },
  {
    key: 'recruitment',
    label: 'Recruitment',
    description: 'High: strong but short-lived trails (gland depletes fast). Low: silent foragers, each ant alone.',
    min: 0,
    max: 100,
    step: 5,
    defaultValue: 50,
    paramMap: [
      {
        targetKey: 'parkington.pheromones.TRAIL_DEPOSIT_AMOUNT',
        mapFn: (s) => lerp3(0.03, 0.15, 0.5, s),
      },
      {
        // Gland capacity
        targetKey: '_gland.capacity',
        mapFn: (s) => lerp3(0.3, 1.0, 2.5, s),
      },
      {
        // Deposit interval (ms) - lower = more frequent deposits
        targetKey: '_deposit.interval',
        mapFn: (s) => lerp3(250, 150, 60, s),
      },
      {
        // Gland cost per deposit
        targetKey: '_gland.costPerDeposit',
        mapFn: (s) => lerp3(0.04, 0.08, 0.16, s),
      },
    ],
  },
  {
    key: 'trailPersistence',
    label: 'Trail Persistence',
    description: 'High: ghost trails to depleted food persist for ages. Low: trails vanish before followers can use them.',
    min: 0,
    max: 100,
    step: 5,
    defaultValue: 50,
    paramMap: [
      {
        // Evaporation rate (inverse - higher persistence = lower evaporation)
        targetKey: 'parkington.pheromones.EVAPORATION_RATE',
        mapFn: (s) => lerp3(0.008, 0.002, 0.0005, s),
      },
      {
        // Diffusion rate
        targetKey: 'parkington.pheromones.DIFFUSION_RATE',
        mapFn: (s) => lerp3(0.005, 0.01, 0.025, s),
      },
    ],
  },
  {
    key: 'scentSensitivity',
    label: 'Scent Sensitivity',
    description: 'High: ants cluster on first signal, amplify noise. Low: pheromone-blind ants that can\'t cooperate.',
    min: 0,
    max: 100,
    step: 5,
    defaultValue: 50,
    paramMap: [
      {
        // Trail follow threshold (lower threshold = more sensitive)
        targetKey: 'parkington.pheromones.TRAIL_FOLLOW_THRESHOLD',
        mapFn: (s) => lerp3(0.15, 0.05, 0.01, s),
      },
      {
        // Confidence gain rate
        targetKey: 'parkington.pheromones.TRAIL_CONFIDENCE_GAIN',
        mapFn: (s) => lerp3(0.02, 0.1, 0.3, s),
      },
      {
        // Trail follow weight in blend
        targetKey: '_blend.trailWeight',
        mapFn: (s) => lerp3(0.1, 0.5, 0.9, s),
      },
    ],
  },
  {
    key: 'homingSkill',
    label: 'Homing Skill',
    description: 'High: ants snap home fast, less exploration time near food. Low: ants wander when returning, many die of exposure.',
    min: 0,
    max: 100,
    step: 5,
    defaultValue: 50,
    paramMap: [
      {
        // Nest gradient weight in return blend
        targetKey: '_blend.nestWeight',
        mapFn: (s) => lerp3(0.1, 0.3, 0.6, s),
      },
      {
        // Direct homing weight in return blend
        targetKey: '_blend.directWeight',
        mapFn: (s) => lerp3(0.15, 0.4, 0.7, s),
      },
    ],
  },
  {
    key: 'edgeAffinity',
    label: 'Edge Affinity',
    description: 'High: ants hug every wall, waste time in dead-end corridors. Low: ants cross open pavement, lose trails easily.',
    min: 0,
    max: 100,
    step: 5,
    defaultValue: 50,
    paramMap: [
      {
        // Thigmotaxis strength
        targetKey: '_blend.edgeWeight',
        mapFn: (s) => lerp3(0.0, 0.05, 0.25, s),
      },
      {
        // Wall follow duration (ms)
        targetKey: '_wallFollow.duration',
        mapFn: (s) => lerp3(800, 2000, 5000, s),
      },
    ],
  },
  {
    key: 'caution',
    label: 'Caution',
    description: 'High: ants avoid all roads, huge detours, fewer trips. Low: ants walk into traffic, mass casualties.',
    min: 0,
    max: 100,
    step: 5,
    defaultValue: 50,
    paramMap: [
      {
        // Alarm avoidance weight
        targetKey: '_blend.alarmWeight',
        mapFn: (s) => lerp3(0.05, 0.3, 0.8, s),
      },
      {
        // Alarm deposit amount
        targetKey: 'parkington.pheromones.ALARM_DEPOSIT_AMOUNT',
        mapFn: (s) => lerp3(0.1, 0.3, 0.6, s),
      },
    ],
  },
];

/**
 * Computed parameter values from trait sliders.
 * Keys starting with '_' are internal blend params (not config registry keys).
 * Keys without '_' prefix map to config registry keys.
 */
export interface ComputedTraitParams {
  // Config registry params
  configParams: Record<string, number>;
  // Internal blend weights (applied directly to ant steering)
  blendParams: {
    noiseWeight: number;
    foodOdorWeight: number;
    trailWeight: number;
    nestWeight: number;
    directWeight: number;
    edgeWeight: number;
    alarmWeight: number;
  };
  // Other internal params
  glandCapacity: number;
  glandCostPerDeposit: number;
  depositInterval: number;
  wallFollowDuration: number;
}

/**
 * Compute all internal parameter values from trait slider positions.
 */
export function computeTraitParams(traits: Record<string, number>): ComputedTraitParams {
  const configParams: Record<string, number> = {};
  const blendParams = {
    noiseWeight: 0.2,
    foodOdorWeight: 0.3,
    trailWeight: 0.5,
    nestWeight: 0.3,
    directWeight: 0.4,
    edgeWeight: 0.05,
    alarmWeight: 0.3,
  };
  let glandCapacity = 1.0;
  let glandCostPerDeposit = 0.08;
  let depositInterval = 150;
  let wallFollowDuration = 2000;

  for (const trait of COLONY_TRAITS) {
    const sliderValue = traits[trait.key] ?? trait.defaultValue;

    for (const mapping of trait.paramMap) {
      const value = mapping.mapFn(sliderValue);

      if (mapping.targetKey.startsWith('_blend.')) {
        const blendKey = mapping.targetKey.slice(7) as keyof typeof blendParams;
        if (blendKey in blendParams) {
          (blendParams as any)[blendKey] = value;
        }
      } else if (mapping.targetKey === '_gland.capacity') {
        glandCapacity = value;
      } else if (mapping.targetKey === '_gland.costPerDeposit') {
        glandCostPerDeposit = value;
      } else if (mapping.targetKey === '_deposit.interval') {
        depositInterval = value;
      } else if (mapping.targetKey === '_wallFollow.duration') {
        wallFollowDuration = value;
      } else {
        // Config registry key
        configParams[mapping.targetKey] = value;
      }
    }
  }

  return { configParams, blendParams, glandCapacity, glandCostPerDeposit, depositInterval, wallFollowDuration };
}

/**
 * Get default trait values (all at 50).
 */
export function getDefaultTraits(): Record<string, number> {
  const defaults: Record<string, number> = {};
  for (const trait of COLONY_TRAITS) {
    defaults[trait.key] = trait.defaultValue;
  }
  return defaults;
}
